/**
 * useAppStore — Backward-compatible wrapper around Zustand global store.
 *
 * This hook provides the EXACT same public API as the original useState-based
 * implementation, but delegates all state management to the Zustand singleton
 * in app/store.ts. This means:
 * - All consumers share the SAME state (no more split-state bugs)
 * - No consumer code changes needed
 * - Side effects (grade sync, heartbeat, midnight reset) remain as useEffects
 *
 * Refactored as part of Account System Refactor — Fase 1 (Zustand Global Store)
 */

import { useEffect, useMemo, useCallback } from 'react'
import { AppState } from '../types'
import { ModuleId } from '../../content'
import { GroupId, GROUPS } from '../../config/systemConfig'
import { resolvePhaseByDate, getModuleMasteryFromFamilies } from '../../mastery'
import { INITIAL_STATE } from '../../config/initialState'
import {
  getActiveAccount,
  isAllDemoAccounts,
  createFreshState,
  createMasteryQueries,
} from '../../accounts'
import type { AccountsState } from '../../accounts'
import type { LearnerAccount } from '../../accounts/types'
import { debug, warn } from '../../utils/logger'
import { useStore } from '../store'

export function useAppStore() {
  // ── Derived State via Selectors ────────────────────────────────────
  const multiAccountState = useStore((s) => s.multiAccountState)
  const isInitialized = useStore((s) => s.isInitialized)
  const viewingLearnerId = useStore((s) => s.viewingLearnerId)
  const selectedGroup = useStore((s) => s.selectedGroup)
  const manualPeriod = useStore((s) => s.manualPeriod)
  const groupVersion = useStore((s) => s.groupVersion)

  // ── Actions via Selectors (stable references) ──────────────────────
  const initialize = useStore((s) => s.initialize)
  const setSelectedGroup = useStore((s) => s.setSelectedGroup)
  const setStartingPeriod = useStore((s) => s.setStartingPeriod)
  const setViewingLearner = useStore((s) => s.setViewingLearner)
  const devResetProgress = useStore((s) => s.devResetProgress)
  const loadDemoAccounts = useStore((s) => s.loadDemoAccounts)
  const updateProfileDisplayName = useStore((s) => s.updateProfileDisplayName)
  const updateAccountEmail = useStore((s) => s.updateAccountEmail)
  const updateAccountNameById = useStore((s) => s.updateAccountNameById)
  const updateTeacherDetails = useStore((s) => s.updateTeacherDetails)
  const updateLearnerDetails = useStore((s) => s.updateLearnerDetails)
  const createAccount = useStore((s) => s.createAccount)
  const deleteAccount = useStore((s) => s.deleteAccount)
  const linkParentToLearnerById = useStore((s) => s.linkParentToLearnerById)
  const linkTeacherToLearnerById = useStore((s) => s.linkTeacherToLearnerById)
  const unlinkParentFromLearner = useStore((s) => s.unlinkParentFromLearner)
  const unlinkTeacherFromLearner = useStore((s) => s.unlinkTeacherFromLearner)
  const setLearnerLinkCode = useStore((s) => s.setLearnerLinkCode)
  const switchAccount = useStore((s) => s.switchAccount)
  const clearActiveAccount = useStore((s) => s.clearActiveAccount)
  const touchLastActive = useStore((s) => s.touchLastActive)
  const resetLearnerProgress = useStore((s) => s.resetLearnerProgress)
  const completeOnboarding = useStore((s) => s.completeOnboarding)
  const createAndLinkStudent = useStore((s) => s.createAndLinkStudent)
  const addClassroom = useStore((s) => s.addClassroom)
  const updateClassroom = useStore((s) => s.updateClassroom)
  const removeClassroom = useStore((s) => s.removeClassroom)
  const addLearnerToClassroom = useStore((s) => s.addLearnerToClassroom)
  const removeLearnerFromClassroom = useStore(
    (s) => s.removeLearnerFromClassroom,
  )
  const joinClassroomByCode = useStore((s) => s.joinClassroomByCode)
  const regenerateJoinCode = useStore((s) => s.regenerateJoinCode)
  const transferClassroomOwnership = useStore(
    (s) => s.transferClassroomOwnership,
  )
  const joinClassroomAsCoTeacher = useStore((s) => s.joinClassroomAsCoTeacher)
  const markCheatSheetViewed = useStore((s) => s.markCheatSheetViewed)
  const recordAnswer = useStore((s) => s.recordAnswer)
  const clearPendingLevelUp = useStore((s) => s.clearPendingLevelUp)
  const devJumpToLevel = useStore((s) => s.devJumpToLevel)
  const savePlacementResult = useStore((s) => s.savePlacementResult)

  // ── Initialize on first render ─────────────────────────────────────
  useEffect(() => {
    initialize()
  }, [initialize])

  const activeAccount = multiAccountState
    ? getActiveAccount(multiAccountState.accounts)
    : null

  const state: AppState = useMemo(() => {
    if (!multiAccountState) return INITIAL_STATE
    if (!activeAccount) return INITIAL_STATE
    if (activeAccount.role === 'learner') {
      return multiAccountState.learnerData[activeAccount.id] || INITIAL_STATE
    }
    if (
      (activeAccount.role === 'parent' ||
        activeAccount.role === 'teacher' ||
        activeAccount.role === 'admin') &&
      viewingLearnerId
    ) {
      return multiAccountState.learnerData[viewingLearnerId] || INITIAL_STATE
    }
    return INITIAL_STATE
  }, [multiAccountState, activeAccount, viewingLearnerId])

  const accounts: AccountsState = multiAccountState
    ? multiAccountState.accounts
    : createFreshState().accounts

  const startingPeriod = manualPeriod || resolvePhaseByDate()

  const currentLearnerDisplayName = useMemo(() => {
    if (!activeAccount || !multiAccountState) return INITIAL_STATE.stats.name
    if (activeAccount.role === 'learner') return activeAccount.displayName
    if (viewingLearnerId) {
      const viewed = multiAccountState.accounts.accountsById[viewingLearnerId]
      if (viewed?.role === 'learner') return viewed.displayName
    }
    return activeAccount.displayName
  }, [activeAccount, multiAccountState, viewingLearnerId])

  const requiresViewingLearnerSelection = Boolean(
    activeAccount &&
      activeAccount.role !== 'learner' &&
      activeAccount.role !== 'admin' &&
      !viewingLearnerId,
  )

  const isDemoMode = useMemo(() => {
    if (!multiAccountState) return false
    return isAllDemoAccounts(multiAccountState.accounts)
  }, [multiAccountState])

  // ── Grade → selectedGroup sync ─────────────────────────────────────
  useEffect(() => {
    if (!isInitialized || !activeAccount) return
    if (activeAccount.role === 'learner' && 'grade' in activeAccount) {
      const learnerGrade = (activeAccount as LearnerAccount).grade
      if (
        learnerGrade &&
        GROUPS.includes(learnerGrade as GroupId) &&
        learnerGrade !== selectedGroup
      ) {
        setSelectedGroup(learnerGrade as GroupId)
        debug(`📚 Auto-synced selectedGroup to learner grade: ${learnerGrade}`)
      }
    }
  }, [
    isInitialized,
    activeAccount?.id,
    activeAccount?.role,
    selectedGroup,
    setSelectedGroup,
  ])

  // ── Keep lastActive fresh ──────────────────────────────────────────
  useEffect(() => {
    if (!isInitialized || !activeAccount) return
    const touch = () => touchLastActive()
    const interval = setInterval(touch, 5 * 60 * 1000)
    const events = ['click', 'keydown', 'scroll', 'touchstart'] as const
    events.forEach((evt) =>
      window.addEventListener(evt, touch, { passive: true }),
    )
    return () => {
      clearInterval(interval)
      events.forEach((evt) => window.removeEventListener(evt, touch))
    }
  }, [isInitialized, activeAccount?.id, touchLastActive])

  // ── Daily Goal Midnight Reset ──────────────────────────────────────
  useEffect(() => {
    if (!isInitialized || !activeAccount || activeAccount.role !== 'learner')
      return
    const checkMidnightReset = () => {
      const currentState = useStore.getState()
      const mas = currentState.multiAccountState
      if (!mas) return
      const learnerState = mas.learnerData[activeAccount.id]
      if (!learnerState || !learnerState.dailyStreak) return
      const today = new Date().toISOString().split('T')[0]
      const lastActive = learnerState.dailyStreak.lastActiveDate
      if (today !== lastActive) {
        const newDailyStreak = {
          ...learnerState.dailyStreak,
          todayQuestionsAnswered: 0,
          todayGoalReached: false,
          lastActiveDate: today,
        }
        const todayDate = new Date()
        const lastActiveDateObj = new Date(lastActive)
        const isNewWeek =
          todayDate.getDay() === 1 &&
          (todayDate.getTime() - lastActiveDateObj.getTime() > 86400000 ||
            lastActiveDateObj.getDay() !== 1)
        if (isNewWeek) {
          newDailyStreak.currentWeekDays = 0
        }
        useStore.setState((prev) => {
          if (!prev.multiAccountState) return prev
          return {
            ...prev,
            multiAccountState: {
              ...prev.multiAccountState,
              learnerData: {
                ...prev.multiAccountState.learnerData,
                [activeAccount.id]: {
                  ...learnerState,
                  dailyStreak: newDailyStreak,
                },
              },
            },
          }
        })
      }
    }
    checkMidnightReset()
    const interval = setInterval(checkMidnightReset, 60 * 1000)
    return () => clearInterval(interval)
  }, [isInitialized, activeAccount?.id, activeAccount?.role])

  // ── Mastery Queries (pure computations from current state) ─────────
  const masteryQueries = createMasteryQueries(
    state,
    selectedGroup,
    manualPeriod,
  )

  // ── Cross-Portal Queries ───────────────────────────────────────────
  const getLearnerData = useCallback(
    (learnerId: string): AppState | null => {
      if (!multiAccountState) return null
      return multiAccountState.learnerData[learnerId] || null
    },
    [multiAccountState],
  )

  const getLearnerModuleMastery = useCallback(
    (learnerId: string, moduleId: ModuleId): number => {
      const data = getLearnerData(learnerId)
      if (!data) return 0
      return getModuleMasteryFromFamilies(
        data.familyMastery || {},
        moduleId,
        selectedGroup,
      )
    },
    [getLearnerData, selectedGroup],
  )

  // ── Public API (IDENTICAL to original) ─────────────────────────────
  return {
    // Derived state
    state,
    accounts,
    activeAccount,
    currentLearnerDisplayName,
    requiresViewingLearnerSelection,
    isInitialized,
    selectedGroup,
    startingPeriod,
    manualPeriod,
    groupVersion,
    viewingLearnerId,
    isDemoMode,
    // Setters
    setSelectedGroup,
    setStartingPeriod,
    setViewingLearner,
    // Cross-portal queries
    getLearnerData,
    getLearnerModuleMastery,
    // Dev/demo
    devResetProgress,
    loadDemoAccounts,
    // Account actions (from Zustand store — shared singleton)
    updateProfileDisplayName,
    updateAccountEmail,
    updateAccountNameById,
    updateTeacherDetails,
    updateLearnerDetails,
    createAccount,
    deleteAccount,
    linkParentToLearnerById,
    linkTeacherToLearnerById,
    unlinkParentFromLearner,
    unlinkTeacherFromLearner,
    setLearnerLinkCode,
    switchAccount,
    clearActiveAccount,
    touchLastActive,
    resetLearnerProgress,
    completeOnboarding,
    createAndLinkStudent,
    addClassroom,
    updateClassroom,
    removeClassroom,
    addLearnerToClassroom,
    removeLearnerFromClassroom,
    joinClassroomByCode,
    regenerateJoinCode,
    transferClassroomOwnership,
    joinClassroomAsCoTeacher,
    // Learner actions (from Zustand store — shared singleton)
    markCheatSheetViewed,
    recordAnswer,
    clearPendingLevelUp,
    devJumpToLevel,
    savePlacementResult,
    // Mastery queries (computed from current state)
    ...masteryQueries,
  }
}
